import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import WorkIcon from "@mui/icons-material/Work";
import SecurityIcon from "@mui/icons-material/Security";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import Input from "../../components/Input";
import DateInput from "../../components/DateInput";
import Dropdown from "../../components/Dropdown";
import moment from "moment";
import {
  userState,
  getUserByIdFromSlice,
  ManagerByDept,
} from "../user/userSlice";
import { getDepartment } from "../department/departmentSlice";
import { ROLE_NAMES } from "../../uitls/Utils";
import {
  getAllUsers,
  addNewUser,
  updateUser,
  getUserById,
  getManagerByDept,
  resetMFA,
} from "../../api/userApi";
import { ROLE_OPTIONS } from "../../uitls/Contant";
import { getAllDepartment } from "../../api/departmentApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import Loader from "../../components/Loader";

const AddEditEmployee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const today = new Date().toISOString().split("T")[0];
  const editEmployeeId = searchParams.get("id");

  // State Management
  const [prevDept, setPrevDept] = useState(null);
  const initialFormValues = {
    firstName: "",
    middleName: "",
    lastName: "",
    corporateEmail: "",
    departmentId: null,
    userRoleId: null,
    designation: null,
    managerId: null,
    dateOfJoining: "",
  };
  const [formValues, setFormValues] = useState(initialFormValues);
  const [formErrors, setFormErrors] = useState({});
  const [managerOptions, setManagerOptions] = useState([]);
  const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
  const [jobTitleOptions, setJobTitleOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResetMfaDialog, setShowResetMfaDialog] = useState(false);
  const [isResettingMfa, setIsResettingMfa] = useState(false);

  // Redux Selectors
  const stateOfUser = useSelector(userState);
  const allDepartments = useSelector(getDepartment);
  const managers = useSelector(ManagerByDept);
  const editEmployee = useSelector(getUserByIdFromSlice);

  // Load departments on mount

  // Load employee data for edit mode
  useEffect(() => {
    if (editEmployeeId) {
      dispatch(getUserById(editEmployeeId));
    } else {
      // Reset form when in add mode
      setFormValues({
        firstName: "",
        middleName: "",
        lastName: "",
        corporateEmail: "",
        departmentId: null,
        userRoleId: null,
        designation: null,
        managerId: null,
        dateOfJoining: "",
      });
      setFormErrors({});
      setPrevDept(null);
      setManagerOptions([]);
      setJobTitleOptions([]);
      setIsSubmitting(false);
    }
  }, [editEmployeeId, dispatch]);

  // Populate departments
  useEffect(() => {
    if (allDepartments && allDepartments.length > 0) {
      
      const departments = allDepartments.map((dept) => ({
        value: dept.id,
        label: dept.departmentName,
      }));
      setBusinessUnitOptions(departments);
    }
  }, [allDepartments]);

  // Helper function to find designation by UUID
  const findDesignationByUuid = (uuid) => {
    for (const dept of allDepartments || []) {
      const foundDesignation = dept.designations?.find(
        (des) => des.id === uuid
      );
      if (foundDesignation) {
        return foundDesignation.name;
      }
    }
    return "N/A";
  };

  // Helper function to process designation data
  const processDesignation = (designation) => {
    if (!designation) {
      return { value: "", label: "" };
    }

    if (typeof designation === "string") {
      return {
        value: designation,
        label: findDesignationByUuid(designation),
      };
    }

    return {
      value: designation.id,
      label: designation.name,
    };
  };

  // Helper function to create form values object
  const createFormValues = (employee) => {
    const designationData = processDesignation(employee.designation);


    return {
      firstName: employee.firstName || "",
      middleName: employee.middleName || "",
      lastName: employee.lastName || "",
      corporateEmail: employee.corporateEmail || "",
      departmentId: employee.department
        ? {
            value: employee.department.id,
            label: employee.department.departmentName,
          }
        : null,
      userRoleId: {
        value: employee.userRoleId,
        label: ROLE_OPTIONS.find((role) => role.value === employee.userRoleId)
          ?.label,
      },
      designation: employee.designation
        ? {
            value: designationData.value,
            label: designationData.label,
          }
        : null,
      managerId: employee.manager
        ? {
            value: employee.manager.id,
            label: employee.manager.name,
          }
        : null,
      dateOfJoining: employee.dateOfJoining || "",
    };
  };

  // Populate form for edit mode
  useEffect(() => {
    if (editEmployee && editEmployeeId) {
      const formValues = createFormValues(editEmployee);
      setFormValues(formValues);
      setPrevDept(editEmployee.department?.id);
    }
  }, [editEmployee, editEmployeeId, allDepartments]);

  // Handle department change and load job titles
  useEffect(() => {
    if (formValues.departmentId?.value && allDepartments?.length > 0) {
      const selectedDept = allDepartments.find(
        (dept) => dept.id === formValues.departmentId.value
      );
      const designations =
        selectedDept?.designations?.map((job) => ({
          value: job.id,
          label: job.name,
        })) || [];

      setJobTitleOptions(designations);

      // Reset designation if department changed
      if (prevDept !== null && prevDept !== formValues.departmentId.value) {
        setFormValues((prev) => ({ ...prev, designation: null }));
        setPrevDept(formValues.departmentId.value);
      } else if (prevDept === null) {
        setPrevDept(formValues.departmentId.value);
      }

      // Update designation label if it's currently showing "Loading..." or UUID
      if (editEmployeeId && formValues.designation?.value) {
        const matchingDesignation = designations.find(
          (job) => job.value === formValues.designation.value
        );
        if (matchingDesignation) {
          console.log("Updating designation label:", {
            from: formValues.designation.label,
            to: matchingDesignation.label,
          });
          setFormValues((prev) => ({
            ...prev,
            designation: {
              value: formValues.designation.value,
              label: matchingDesignation.label,
            },
          }));
        } else {
          console.log(
            "No matching designation found for:",
            formValues.designation.value
          );
        }
      }
    } else {
      setJobTitleOptions([]);
    }
  }, [formValues.departmentId?.value, allDepartments, editEmployeeId]);

  // Load managers based on department and role
  useEffect(() => {
    if (formValues.departmentId?.value && formValues.userRoleId?.value) {
      dispatch(
        getManagerByDept({
          deptId: formValues.departmentId.value,
          roleId: formValues.userRoleId.value,
        })
      );
    }
  }, [formValues.departmentId?.value, formValues.userRoleId?.value, dispatch]);

  // Populate manager options
  useEffect(() => {
    if (managers && managers.length > 0) {
      // Filter to only include users with manager role and map to options
      const managerRoleId = ROLE_NAMES.Manager; // Use ROLE_NAMES.Manager for consistency

      const options = managers
        .filter((manager) => manager.userRoleId === managerRoleId) // Only include managers
        .map((manager) => ({
          value: manager.id,
          label:
            manager.firstName +
            (manager.lastName ? ` ${manager.lastName}` : ""),
        }));

      console.log("Manager options:", options); // Debug log
      setManagerOptions(options);
    } else {
      setManagerOptions([]);
    }
  }, [managers]);

  // Memoize role options
  const roleOptions = useMemo(
    () =>
      ROLE_OPTIONS.map((role) => ({
        value: role.value,
        label: role.label,
      })),
    []
  );

  // Event Handlers
  const handleDateChange = useCallback((date) => {
    if (date) {
      setFormValues((prev) => ({
        ...prev,
        dateOfJoining: moment(date).format("YYYY-MM-DD"),
      }));
      setFormErrors((prev) => ({ ...prev, dateOfJoining: "" }));
    } else {
      setFormValues((prev) => ({
        ...prev,
        dateOfJoining: "",
      }));
    }
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (value?.trim()) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }, []);

  const handleDropdownChange = useCallback(
    (name) => (value) => {
      if (name === "departmentId") {
        setFormValues((prev) => ({
          ...prev,
          designation: null,
          managerId: null,
          [name]: value,
        }));
      } else if (name === "userRoleId") {
        setFormValues((prev) => ({
          ...prev,
          managerId: null,
          [name]: value,
        }));
      } else {
        setFormValues((prev) => ({ ...prev, [name]: value }));
      }

      if (value?.value) {
        setFormErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    []
  );

  const validateForm = () => {
    const errors = {};

    // Personal Information validation
    if (!formValues.firstName?.trim()) {
      errors.firstName = "First Name is required";
    }
    if (!formValues.lastName?.trim()) {
      errors.lastName = "Last Name is required";
    }
    if (!formValues.corporateEmail?.trim()) {
      errors.corporateEmail = "Organizational Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formValues.corporateEmail)) {
      errors.corporateEmail = "Invalid email format";
    }

    // Employment Information validation
    if (!formValues.departmentId?.value) {
      errors.departmentId = "Business Unit is required";
    }
    if (!formValues.userRoleId?.value) {
      errors.userRoleId = "Role is required";
    }
    if (!formValues.designation?.value) {
      errors.designation = "Job Title is required";
    }
    if (
      formValues.userRoleId?.value === ROLE_NAMES.User &&
      !formValues.managerId?.value
    ) {
      errors.managerId = "Manager is required";
    }
    if (!formValues.dateOfJoining?.trim()) {
      errors.dateOfJoining = "Date of Joining is required";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedDepartment = allDepartments.find(
        (dept) => dept.id === formValues.departmentId.value
      );

      const payload = {
        ...formValues,
        departmentId: selectedDepartment,
        deptId: formValues.departmentId.value,
        userRoleId: formValues.userRoleId.value,
        designation: formValues.designation.value,
        managerId: formValues.managerId?.value,
      };

      if (editEmployeeId) {
        await dispatch(
          updateUser({
            userId: editEmployeeId,
            values: trimObjectValues(payload),
          })
        ).unwrap();
      } else {
        await dispatch(addNewUser(trimObjectValues(payload))).unwrap();
      }

      // Refresh employee list
      dispatch(getAllUsers());

      // Navigate back to employee list
      navigate("/admin/employee");
    } catch (error) {
      console.error("Error saving employee:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetMfa = async () => {
    setIsResettingMfa(true);
    try {
      await dispatch(resetMFA(editEmployeeId)).unwrap();

      // Refresh employee data to get updated MFA status
      dispatch(getUserById(editEmployeeId));

      setShowResetMfaDialog(false);
    } catch (error) {
      console.error("Error resetting MFA:", error);
    } finally {
      setIsResettingMfa(false);
    }
  };

  const resetForm = () => {
    if (!editEmployeeId) {
      setFormValues(initialFormValues);
      setFormErrors({});
      setPrevDept(null);
      setManagerOptions([]);
      setJobTitleOptions([]);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
      {stateOfUser.loading && <Loader />}

      {/* Back Navigation Button */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/admin/employee")}
          className="flex items-center gap-2 text-[#135e96] hover:text-[#0b3a5c] font-semibold transition-colors duration-200 group"
        >
          <ArrowBackIos className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          <span>Back</span>
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Personal Information Card */}
        <Card className="mb-6 shadow-lg rounded-2xl overflow-hidden">
          <div className="brand-header py-4 px-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <PersonIcon className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Personal Information
                </h3>
                <p className="text-blue-100 text-sm">
                  Basic employee details and contact information
                </p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                name="firstName"
                placeholder="Enter first name"
                value={formValues.firstName}
                onChange={handleInputChange}
                label="First Name"
                requiredField={true}
                error={formErrors.firstName || ""}
              />
              <Input
                name="middleName"
                placeholder="Enter middle name (optional)"
                value={formValues.middleName}
                onChange={handleInputChange}
                label="Middle Name"
                error={formErrors.middleName || ""}
              />
              <Input
                name="lastName"
                placeholder="Enter last name"
                value={formValues.lastName}
                onChange={handleInputChange}
                label="Last Name"
                requiredField={true}
                error={formErrors.lastName || ""}
              />
              <Input
                name="corporateEmail"
                placeholder="employee@company.com"
                value={formValues.corporateEmail}
                onChange={handleInputChange}
                label="Organizational Email"
                type="email"
                requiredField={true}
                error={formErrors.corporateEmail || ""}
              />
            </div>
          </CardContent>
        </Card>

        {/* Employment Information Card */}
        <Card className="mb-6 shadow-lg rounded-2xl overflow-hidden">
          <div className="brand-header py-4 px-6">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <WorkIcon className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">
                  Employment Information
                </h3>
                <p className="text-blue-100 text-sm">
                  Job role, department, and organizational details
                </p>
              </div>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Dropdown
                submitRef={true}
                defaultValue={formValues.departmentId}
                options={businessUnitOptions}
                handleChange={handleDropdownChange("departmentId")}
                errorMessage={formErrors.departmentId || ""}
                requiredField={true}
                defaultLabel="Choose a business unit"
                label="Business Unit"
                size="medium"
              />
              <Dropdown
                submitRef={true}
                defaultValue={formValues.userRoleId}
                options={roleOptions}
                handleChange={handleDropdownChange("userRoleId")}
                errorMessage={formErrors.userRoleId || ""}
                requiredField={true}
                defaultLabel="Select employee role"
                label="Role"
                size="medium"
              />
              <Dropdown
                submitRef={true}
                defaultValue={formValues.designation}
                options={jobTitleOptions}
                handleChange={handleDropdownChange("designation")}
                errorMessage={formErrors.designation || ""}
                requiredField={true}
                defaultLabel={(() => {
                  if (!formValues.departmentId?.value) {
                    return "Select a business unit first";
                  }
                  if (jobTitleOptions.length === 0) {
                    return "No job titles available";
                  }
                  return "Choose job title";
                })()}
                label="Job Title"
                size="medium"
              />
              <Dropdown
                submitRef={true}
                defaultValue={formValues.managerId}
                options={managerOptions}
                handleChange={handleDropdownChange("managerId")}
                errorMessage={formErrors.managerId || ""}
                requiredField={formValues.userRoleId?.value === ROLE_NAMES.User}
                defaultLabel={(() => {
                  if (
                    !formValues.departmentId?.value ||
                    !formValues.userRoleId?.value
                  ) {
                    return "Select department & role first";
                  }
                  if (managerOptions.length === 0) {
                    return "No managers available";
                  }
                  return "Select reporting manager";
                })()}
                label="Manager"
                size="medium"
              />
              <div className="flex flex-col">
                <label className="block text-sm font-semibold mb-1.5 text-gray-700">
                  Joining Date
                  <span className="text-red-500 text-base ml-1">*</span>
                </label>
                <DateInput
                  id="end-date-picker"
                  selected={formValues.dateOfJoining ? moment(formValues.dateOfJoining, "YYYY-MM-DD").toDate() : null}
                  onChange={handleDateChange}
                  placeholderText="mm/dd/yyyy"
                  dateFormat="MM/dd/yyyy"
                  className="w-full"
                  maxDate={new Date()}
                  calendarClassName="shadow-lg border-2 border-gray-200 rounded-xl"
                  popperClassName="z-50"
                />
                {formErrors.dateOfJoining && (
                  <div className="mt-0.5 min-h-[0.875rem] flex items-center gap-2 text-xs text-[#ef4444]">
                    <svg
                      className="w-4 h-4 text-red-500 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>{formErrors.dateOfJoining}</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* MFA Status Card - Only show in edit mode */}
        {editEmployeeId && editEmployee && (
          <Card className="mb-6 shadow-lg rounded-2xl overflow-hidden">
            <div className="brand-header py-4 px-6">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <SecurityIcon className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    Multi-Factor Authentication (MFA)
                  </h3>
                  <p className="text-white text-sm">
                    Manage employee's MFA settings
                  </p>
                </div>
              </div>
            </div>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div>
                    <Typography
                      variant="body1"
                      className="font-medium text-gray-700 mb-1"
                    >
                      MFA Status
                    </Typography>
                    <Chip
                      label={editEmployee.mfaEnabled ? "Enabled" : "Disabled"}
                      color={editEmployee.mfaEnabled ? "success" : "default"}
                      variant="outlined"
                      size="medium"
                    />
                  </div>
                  {editEmployee.mfaEnabled && (
                    <Alert severity="info" className="flex-1">
                      <Typography variant="body2">
                        This employee has MFA enabled. They will need to use an
                        authenticator app to log in.
                      </Typography>
                    </Alert>
                  )}
                </div>
                {editEmployee.mfaEnabled && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<WarningIcon />}
                    onClick={() => setShowResetMfaDialog(true)}
                    disabled={isResettingMfa}
                    size="medium"
                  >
                    Reset MFA
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-4 mt-6">
          <Button
            variant="outlined"
            onClick={resetForm}
            disabled={isSubmitting || editEmployeeId}
            size="large"
            sx={{
              borderColor: "#135e96",
              color: "#135e96",
              fontWeight: 600,
              '&:hover': {
                borderColor: '#0b3a5c',
                backgroundColor: 'rgba(19,94,150,0.06)'
              },
              '&:disabled': {
                opacity: 0.5
              }
            }}
          >
            Reset the form
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            size="large"
            sx={{
              background: "#135e96",
              boxShadow: "0 4px 14px 0 rgba(19, 94, 150, 0.35)",
              fontWeight: 700,
              '&:hover': {
                background: '#0b3a5c',
                boxShadow: '0 6px 20px rgba(19, 94, 150, 0.45)'
              }
            }}
          >
            {(() => {
              if (isSubmitting) {
                return editEmployeeId ? "Updating..." : "Adding...";
              }
              return editEmployeeId ? "Update Employee" : "Add Employee";
            })()}
          </Button>
        </div>
      </form>

      {/* Reset MFA Confirmation Dialog */}
      <Dialog
        open={showResetMfaDialog}
        onClose={() => setShowResetMfaDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className="flex items-center space-x-2">
          <WarningIcon color="error" />
          <Typography variant="h6">Reset MFA</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" className="mb-4">
            Are you sure you want to reset MFA for {`${editEmployee?.firstName} ${editEmployee?.lastName}`}?
          </Typography>
          <Alert severity="warning" className="mb-4">
            <Typography variant="body2">
              <strong>Warning:</strong> This action will:
            </Typography>
            <ul className="mt-2 ml-4 list-disc">
              <li>Disable MFA for this employee</li>
              <li>Remove their authenticator app setup</li>
              <li>Allow them to log in with just email and password</li>
              <li>Require them to set up MFA again if needed</li>
            </ul>
          </Alert>
          <Typography variant="body2" className="text-gray-600">
            This action cannot be undone. The employee will need to set up MFA
            again if required.
          </Typography>
        </DialogContent>
        <DialogActions className="p-4">
          <Button
            onClick={() => setShowResetMfaDialog(false)}
            disabled={isResettingMfa}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetMfa}
            disabled={isResettingMfa}
            variant="contained"
            color="error"
            startIcon={
              isResettingMfa ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <WarningIcon />
              )
            }
          >
            {isResettingMfa ? "Resetting..." : "Reset MFA"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AddEditEmployee;
